import { type Ref, ref, computed } from "vue";

import apiGraph from "@/helpers/api/apiGraph";
import apiSharePoint from "@/helpers/api/apiSharePoint";

import Permission from "@/models/Documents/Permission";
import DriveItem from "@/models/Documents/DriveItem";

import TrackHelper from "@/helpers/utilities/TrackHelper";
import { useMeStore } from "@/stores/me";

interface GraphPermission {
  id?: string;
  roles?: string[];
  grantedToV2?: {
    user?: { id?: string; displayName?: string; email?: string };
    siteUser?: { loginName?: string; displayName?: string };
  };
}

const isCurrentUser = (
  permission: GraphPermission,
  currentUserId: string | null,
  currentUserEmail: string
): boolean => {
  const user = permission.grantedToV2?.user;
  const siteEmail = permission.grantedToV2?.siteUser?.loginName?.split("|")[2];
  const normalizedCurrentEmail = currentUserEmail.toLowerCase();

  if (user?.id && user.id === currentUserId) return true;
  if (user?.email && user.email.toLowerCase() === normalizedCurrentEmail) return true;
  if (siteEmail && siteEmail.toLowerCase() === normalizedCurrentEmail) return true;
  return false;
};

const extractPermissionKeys = (permission: GraphPermission): string[] => {
  const user = permission.grantedToV2?.user;
  const siteEmail = permission.grantedToV2?.siteUser?.loginName?.split("|")[2];

  const keys: string[] = [];
  if (user?.id) keys.push(user.id);
  if (user?.email) keys.push(user.email);
  if (siteEmail && siteEmail !== user?.email) keys.push(siteEmail);
  return keys;
};

const collectPermissionKeys = (
  permission: GraphPermission,
  currentUserId: string | null,
  currentUserEmail: string
): string[] => {
  if (isCurrentUser(permission, currentUserId, currentUserEmail)) return [];
  return extractPermissionKeys(permission);
};

interface PermissionsComposableDependencies {
  calculateRootOrDriveId: (driveId: string | null | undefined) => string | null | undefined;
  // Lazy lookup so the composable can reference a getter defined later in the
  // store setup (calculateRootOrItemId reads sharedDrives + legacy state that
  // remains in index.ts for now).
  getCalculateRootOrItemId: () => (driveId: string, itemId: string) => string;
  loadDriveItem: (driveId: string, itemId: string) => Promise<DriveItem>;
}

const calculatePermissionsFromSharePointPermissionObject = (permissionObject: { Low: number }) => {
  if ((permissionObject.Low & (1 << (4 - 1))) != 0) {
    return new Permission(null, null, null, Permission.nctPermission.OWNER);
  } else if ((permissionObject.Low & (1 << (3 - 1))) != 0) {
    return new Permission(null, null, null, Permission.nctPermission.WRITE);
  } else if ((permissionObject.Low & (1 << (1 - 1))) != 0) {
    return new Permission(null, null, null, Permission.nctPermission.READ);
  } else {
    return new Permission(null, null, null, Permission.nctPermission.None);
  }
};

export const usePermissionsComposable = (dependencies: PermissionsComposableDependencies) => {
  const itemPermissionsMap: Ref<Record<string, Record<string, Permission>>> = ref({});
  const sharedWithPermissionsMap: Ref<Record<string, Record<string, Record<string, Permission>>>> = ref({});

  const myItemPermission = computed(() => (driveId: string, itemId: string) => {
    const realDriveId = dependencies.calculateRootOrDriveId(driveId);
    if (itemPermissionsMap.value[realDriveId as string]) {
      if (itemPermissionsMap.value[realDriveId as string][itemId]) {
        return itemPermissionsMap.value[realDriveId as string][itemId];
      }
    }
    return null;
  });

  const sharedWithPermission = computed(() => (driveId: string, itemId: string) => {
    return sharedWithPermissionsMap.value[driveId]?.[itemId] ?? {};
  });

  const reloadDocumentPermissions = async (driveId: string, itemId: string) => {
    if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
      const response = await apiGraph.get(`/drives/${driveId}/items/${itemId}/permissions`).catch(() => {
        if (!itemPermissionsMap.value[driveId]) {
          itemPermissionsMap.value[driveId] = {};
        }
        itemPermissionsMap.value[driveId][itemId] = new Permission(null, null, null, Permission.nctPermission.None);
      });

      if (response && (response as { data?: unknown }).data) {
        if (!itemPermissionsMap.value[driveId]) {
          itemPermissionsMap.value[driveId] = {};
        }
        itemPermissionsMap.value[driveId][itemId] = calculatePermissionsFromSharePointPermissionObject(
          (response as { data: { Low: number } }).data
        );
      }
    } else {
      const driveItemFetched = await dependencies.loadDriveItem(driveId, itemId);

      if (driveItemFetched && driveItemFetched.webUrl && driveItemFetched.webUrl.length > 0) {
        const regexSite = /^((https?:\/\/?[^:/]*\/sites\/[^:/]*\/)|(https?:\/\/?[^:/]*)).*$/g;
        const regexSiteAndFiles = /(\/\/[a-zA-Z0-9.-]+)([/sites|personal]?\/[^?]+)/g;
        const regexForSharePointFolderUrl = /^https:\/\/([^/]+)(\/(?:personal|sites)\/[^/]+)((?:\/[^/]+)*)$/;
        const regexForSharePointFileUrl =
          /^https:\/\/([^/]+)(\/(?:personal|sites)\/[^/]+)((?:\/[^/?]+)*)(?:\?[^#]*?\bsourcedoc=%7B([^&#]+))?%7D/;

        let result: { data?: { Low: number; vti_x005f_listtitle?: string } } | undefined;
        if (driveItemFetched.webUrl.includes("/personal/")) {
          if (
            (driveItemFetched.folder || driveItemFetched.isFolder) &&
            !driveItemFetched.root &&
            !driveItemFetched.package
          ) {
            const urlSplit = regexForSharePointFolderUrl.exec(driveItemFetched.webUrl)!;
            result = await apiSharePoint.get(
              `https://${urlSplit[1]}${urlSplit[2]}/_api/web/getFolderByServerRelativeUrl('${urlSplit[2]}${urlSplit[3]}')/ListItemAllFields/EffectiveBasePermissions`
            );
          } else if (driveItemFetched.root) {
            const urlSplit = regexForSharePointFolderUrl.exec(driveItemFetched.webUrl)!;
            result = await apiSharePoint.get(`https://${urlSplit[1]}${urlSplit[2]}/_api/web/EffectiveBasePermissions`);
          } else {
            const urlSplitO365File = regexForSharePointFileUrl.exec(driveItemFetched.webUrl);
            if (urlSplitO365File) {
              result = await apiSharePoint.get(
                `https://${urlSplitO365File[1]}${urlSplitO365File[2]}/_api/web/getfilebyid('${urlSplitO365File[4]}')/ListItemAllFields/EffectiveBasePermissions`
              );
            } else {
              const urlSplitOtherFile = regexForSharePointFolderUrl.exec(driveItemFetched.webUrl)!;
              result = await apiSharePoint.get(
                `https://${urlSplitOtherFile[1]}${urlSplitOtherFile[2]}/_api/web/getFolderByServerRelativeUrl('${urlSplitOtherFile[2]}${urlSplitOtherFile[3]}')/ListItemAllFields/EffectiveBasePermissions`
              );
            }
          }
        } else {
          if (
            (driveItemFetched.folder || driveItemFetched.isFolder) &&
            !driveItemFetched.root &&
            !driveItemFetched.package
          ) {
            const urlSplit = regexForSharePointFolderUrl.exec(driveItemFetched.webUrl)!;
            result = await apiSharePoint.get(
              `https://${urlSplit[1]}${urlSplit[2]}/_api/web/getFolderByServerRelativeUrl('${urlSplit[2]}${urlSplit[3]}')/ListItemAllFields/EffectiveBasePermissions`
            );
          } else if (driveItemFetched.root) {
            TrackHelper.registerMessage("Root drive item url: " + driveItemFetched.webUrl);
            const urlSplit = regexSite.exec(driveItemFetched.webUrl)!;
            const urlSplitFiles = regexSiteAndFiles.exec(driveItemFetched.webUrl)!;
            const propertiesResponse = await apiSharePoint.get(
              `${urlSplit[1]}/_api/web/getFolderByServerRelativeUrl('${urlSplitFiles[2]}')/Properties`
            );
            if (propertiesResponse && propertiesResponse.data && propertiesResponse.data.vti_x005f_listtitle) {
              result = await apiSharePoint.get(
                `${urlSplit[1]}/_api/web/getListByTitle('${propertiesResponse.data.vti_x005f_listtitle}')/EffectiveBasePermissions`
              );
            }
          } else {
            const urlSplitO365File = regexForSharePointFileUrl.exec(driveItemFetched.webUrl);
            if (urlSplitO365File) {
              result = await apiSharePoint.get(
                `https://${urlSplitO365File[1]}${urlSplitO365File[2]}/_api/web/getfilebyid('${urlSplitO365File[4]}')/ListItemAllFields/EffectiveBasePermissions`
              );
            } else {
              const urlSplitOtherFile = regexForSharePointFolderUrl.exec(driveItemFetched.webUrl)!;
              result = await apiSharePoint.get(
                `https://${urlSplitOtherFile[1]}${urlSplitOtherFile[2]}/_api/web/getFolderByServerRelativeUrl('${urlSplitOtherFile[2]}${urlSplitOtherFile[3]}')/ListItemAllFields/EffectiveBasePermissions`
              );
            }
          }
        }

        if (result && result.data) {
          if (!itemPermissionsMap.value[driveId]) {
            itemPermissionsMap.value[driveId] = {};
          }
          itemPermissionsMap.value[driveId][itemId] = calculatePermissionsFromSharePointPermissionObject(result.data);
        }
      } else {
        if (!itemPermissionsMap.value[driveId]) {
          itemPermissionsMap.value[driveId] = {};
        }
        itemPermissionsMap.value[driveId][itemId] = new Permission();
      }
    }
    return itemPermissionsMap.value[driveId][itemId];
  };

  const loadDocumentPermissions = async (driveId: string, itemId: string) => {
    const calculateRootOrItemId = dependencies.getCalculateRootOrItemId();
    const calcItemId = calculateRootOrItemId(driveId, itemId);
    if (!myItemPermission.value(driveId, calcItemId)) {
      await reloadDocumentPermissions(driveId, itemId);
    }
    return myItemPermission.value(driveId, calcItemId);
  };

  const storePermissionsForItem = (
    driveId: string,
    itemId: string,
    permissions: GraphPermission[],
    currentUserId: string | null,
    currentUserEmail: string
  ) => {
    const permissionsByPerson: Record<string, Permission> = {};
    for (const permission of permissions) {
      const permissionInstance = new Permission(permission);
      const keys = collectPermissionKeys(permission, currentUserId, currentUserEmail);
      for (const key of keys) {
        permissionsByPerson[key] = permissionInstance;
      }
    }

    if (!sharedWithPermissionsMap.value[driveId]) {
      sharedWithPermissionsMap.value[driveId] = {};
    }
    sharedWithPermissionsMap.value[driveId][itemId] = permissionsByPerson;
  };

  const reloadSharedWithPermissions = async (driveId: string, itemId: string) => {
    const meStore = useMeStore();
    const response = await apiGraph.get(`/drives/${driveId}/items/${itemId}/permissions`);
    storePermissionsForItem(
      driveId,
      itemId,
      response.data.value as GraphPermission[],
      meStore.extSysApiUserId,
      meStore.emailSchoolAddress
    );
  };

  const reloadSharedWithPermissionsBatch = async (items: Array<{ driveId: string; itemId: string }>) => {
    const meStore = useMeStore();

    const allRequests = items
      .filter(item => item.driveId && item.itemId)
      .map(item => Permission.generateGraphRequestFromDriveItem(item.driveId, item.itemId));

    const chunks: (typeof allRequests)[] = [];
    for (let chunkStart = 0; chunkStart < allRequests.length; chunkStart += 20) {
      chunks.push(allRequests.slice(chunkStart, chunkStart + 20));
    }

    const batchResults = await Promise.allSettled(chunks.map(chunk => apiGraph.post("/$batch", { requests: chunk })));

    for (const batchResult of batchResults) {
      if (batchResult.status !== "fulfilled") continue;

      for (const innerResponse of batchResult.value.data.responses) {
        if (innerResponse.status !== 200) continue;

        const [driveId, itemId] = innerResponse.id.split("|");
        storePermissionsForItem(
          driveId,
          itemId,
          innerResponse.body.value as GraphPermission[],
          meStore.extSysApiUserId,
          meStore.emailSchoolAddress
        );
      }
    }
  };

  return {
    itemPermissionsMap,
    sharedWithPermissionsMap,
    myItemPermission,
    sharedWithPermission,
    loadDocumentPermissions,
    reloadDocumentPermissions,
    reloadSharedWithPermissions,
    reloadSharedWithPermissionsBatch
  };
};
